import axios from 'axios';
import API_CONFIG from '../config/api.config';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS
});

const cmsClient = axios.create({
  baseURL: API_CONFIG.CMS_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we have a token (meaning user was authenticated)
      // or if we're on an admin route
      const token = localStorage.getItem('authToken');
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      
      if (token || isAdminRoute) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API Service Methods
const apiService = {
  // Public Stats & Data
  getPublicStats: () => apiClient.get('/public/stats'),
  getPublicSliderImages: () => apiClient.get('/public/slider-images'),

  // Contact
  submitContact: (data) => apiClient.post('/contact', data),

  // Gallery
  getGalleries: () => apiClient.get('/galleries'),
  getGalleryById: (id) => apiClient.get(`/galleries/${id}`),

  // Blog
  getBlogs: () => apiClient.get('/blogs'),
  getBlogById: (id) => apiClient.get(`/blogs/${id}`),
  getCmsBlogCategories: () => cmsClient.get(`/sites/${API_CONFIG.CMS_SITE_SLUG}/blog/categories`),
  getCmsBlogs: (params = {}) => cmsClient.get(`/sites/${API_CONFIG.CMS_SITE_SLUG}/blog/posts`, { params }),
  getCmsBlogBySlug: (slug) => cmsClient.get(`/sites/${API_CONFIG.CMS_SITE_SLUG}/blog/posts/${slug}`),
  getCmsGalleryCategories: () => cmsClient.get(`/sites/${API_CONFIG.CMS_SITE_SLUG}/gallery/categories`),
  getCmsGalleryPhotos: (params = {}) => cmsClient.get(`/sites/${API_CONFIG.CMS_SITE_SLUG}/gallery/photos`, { params }),
  getCmsGalleryPhotoBySlug: (slug) => cmsClient.get(`/sites/${API_CONFIG.CMS_SITE_SLUG}/gallery/photos/${slug}`),

  // Visitor
  registerVisitor: (data) => {
    const uploadTimeout = 90000;
    if (data instanceof FormData) {
      return apiClient.post('/visitors', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: uploadTimeout
      });
    }
    return apiClient.post('/visitors', data, { timeout: uploadTimeout });
  },
  getVisitors: () => apiClient.get('/visitors'),

  // Stall
  bookStall: (data) => apiClient.post('/stalls', data),

  // Exhibition
  getExhibitions: () => apiClient.get('/exhibitions'),
  getExhibitionById: (id) => apiClient.get(`/exhibitions/${id}`),

  // Slider
  getSliders: () => apiClient.get('/sliders'),

  // Settings/Config
  getSettings: () => apiClient.get('/settings'),

  // Auth
  login: (credentials) => apiClient.post('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  getProfile: () => apiClient.get('/auth/profile'),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  changePassword: (data) => apiClient.put('/auth/password', data),

  // Admin - Dashboard Stats
  getStats: () => apiClient.get('/admin/stats'),

  // Admin - Enquiries
  getEnquiries: () => apiClient.get('/admin/enquiries'),
  getEnquiryById: (id) => apiClient.get(`/admin/enquiries/${id}`),
  deleteEnquiry: (id) => apiClient.delete(`/admin/enquiries/${id}`),

  // Admin - Visitors Management
  getAdminVisitors: () => apiClient.get('/admin/visitors', { params: { _t: Date.now() } }),
  getAdminVisitorById: (id) => apiClient.get(`/admin/visitors/${id}`, { params: { _t: Date.now() } }),
  createVisitor: (data) => apiClient.post('/admin/visitors', data),
  updateVisitor: (id, data) => apiClient.put(`/admin/visitors/${id}`, data),
  deleteVisitor: (id) => apiClient.delete(`/admin/visitors/${id}`),
  bulkDeleteVisitors: (ids) => apiClient.post('/admin/visitors/bulk-delete', { ids }),
  sendSMS: (visitorId, data) => apiClient.post(`/admin/visitors/${visitorId}/send-sms`, data),
  sendWhatsApp: (visitorId, data) => apiClient.post(`/admin/visitors/${visitorId}/send-whatsapp`, data),
  getCountries: () => apiClient.get('/admin/countries'),
  getStates: (countryId) => apiClient.get('/admin/states', { params: { country_id: countryId } }),
  getCities: (stateId) => apiClient.get('/admin/cities', { params: { state_id: stateId } }),
  getExhibitionLocations: () => apiClient.get('/admin/exhibition-locations'),

  // Admin - Print Template Management
  getVisitorBadgeTemplate: () => apiClient.get('/print-templates/visitor-badge', { params: { _t: Date.now() } }),
  saveVisitorBadgeTemplate: (templateHtml) => apiClient.post('/print-templates/visitor-badge', { bodyOfTheTemplate: templateHtml }),
  deleteVisitorBadgeTemplate: () => apiClient.delete('/print-templates/visitor-badge'),

  // Admin - Blog Management
  getAdminBlogs: () => apiClient.get('/admin/blogs'),
  getAdminBlogById: (id) => apiClient.get(`/admin/blogs/${id}`),
  createBlog: (data) => apiClient.post('/admin/blogs', data),
  updateBlog: (id, data) => apiClient.put(`/admin/blogs/${id}`, data),
  deleteBlog: (id) => apiClient.delete(`/admin/blogs/${id}`),

  // Admin - Gallery Management
  getAdminGalleries: () => apiClient.get('/admin/galleries'),
  getAdminGalleryById: (id) => apiClient.get(`/admin/galleries/${id}`),
  createGallery: (data) => apiClient.post('/admin/galleries', data),
  updateGallery: (id, data) => apiClient.put(`/admin/galleries/${id}`, data),
  deleteGallery: (id) => apiClient.delete(`/admin/galleries/${id}`),

  // Admin - Gallery Photos Management
  getGalleryPhotos: (categoryId) => apiClient.get(`/admin/galleries/${categoryId}/photos`),
  uploadGalleryPhoto: (categoryId, formData) => apiClient.post(`/admin/galleries/${categoryId}/photos`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  updateGalleryPhoto: (categoryId, photoId, data) => {
    if (data instanceof FormData) {
      return apiClient.put(`/admin/galleries/${categoryId}/photos/${photoId}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.put(`/admin/galleries/${categoryId}/photos/${photoId}`, data);
  },
  deleteGalleryPhoto: (categoryId, photoId) => apiClient.delete(`/admin/galleries/${categoryId}/photos/${photoId}`),

  // Admin - Exhibition Management
  getAdminExhibitions: () => apiClient.get('/admin/exhibitions'),
  getAdminExhibitionById: (id) => apiClient.get(`/admin/exhibitions/${id}`),
  createExhibition: (data) => apiClient.post('/admin/exhibitions', data),
  updateExhibition: (id, data) => apiClient.put(`/admin/exhibitions/${id}`, data),
  deleteExhibition: (id) => apiClient.delete(`/admin/exhibitions/${id}`),

  // Admin - Notifications
  getNotifications: () => apiClient.get('/admin/notifications'),
  createNotification: (data) => apiClient.post('/admin/notifications', data),
  deleteNotification: (id) => apiClient.delete(`/admin/notifications/${id}`),

  // Admin - Banner Management
  getBanners: () => apiClient.get('/admin/banners'),
  uploadBanner: (formData) => apiClient.post('/admin/banners', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  updateBannerStatus: (id, status) => apiClient.put(`/admin/banners/${id}/status`, { status }),
  deleteBanner: (id) => apiClient.delete(`/admin/banners/${id}`),

  // Public Banners
  getPublicBanners: () => apiClient.get('/banners'),

  // Terms & Conditions
  getTerms: () => apiClient.get('/terms'),
  getAdminTerms: () => apiClient.get('/admin/terms'),
  updateTerms: (terms) => apiClient.put('/admin/terms', { terms }),

  // File Upload
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export default apiService;
